import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  MessageCircle,
  ShieldCheck,
  Zap,
  Smartphone,
  Wallet,
  Receipt,
  AlertOctagon,
  ShoppingBag,
  TruckIcon,
  UserX,
  LayoutDashboard,
  Plus,
  Minus,
  Sparkles,
  TrendingUp,
  BadgeCheck,
  FileSearch,
} from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import MarketingFooter from "../components/MarketingFooter";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";

/* ------------------------------------------------------------------ */
/* Static content                                                      */
/* ------------------------------------------------------------------ */

const TRUST_PILLS = [
  "IRA-aligned",
  "7-day cooling-off",
  "WhatsApp-first",
  "KES 99 / week",
];

const HOW_STEPS = [
  {
    n: "01",
    title: "Connect your merchant account",
    body: "Sign up via WhatsApp or our portal. Verify your M-Pesa till, POS, or social-commerce handle.",
  },
  {
    n: "02",
    title: "Choose weekly cover",
    body: "Pick a tier that matches your daily volume. Premiums billed weekly — no annual lock-in.",
  },
  {
    n: "03",
    title: "Report fraud instantly",
    body: "Send screenshots, transaction IDs, and a short note via WhatsApp. We acknowledge in seconds.",
  },
  {
    n: "04",
    title: "Get verified and paid fast",
    body: "FlashClaim auto-approves clean claims; edge cases route to our SIU team. Funds back via M-Pesa B2C.",
  },
];

const TIERS = [
  {
    code: "starter",
    name: "Starter",
    premium: 99,
    cover: 50000,
    bestFor: "Mama-mboga, kibanda, kiosk owners",
    perks: [
      "M-Pesa reversal fraud",
      "Phishing & ATO loss",
      "WhatsApp claim filing",
      "Up to KES 50,000 cover",
    ],
  },
  {
    code: "growth",
    name: "Growth",
    premium: 249,
    cover: 250000,
    bestFor: "Online sellers, IG shops, social-commerce",
    perks: [
      "Everything in Starter",
      "Fake-customer dispute cover",
      "Priority FlashClaim queue",
      "Up to KES 250,000 cover",
    ],
    featured: true,
  },
  {
    code: "business",
    name: "Business",
    premium: 499,
    cover: 1000000,
    bestFor: "POS agents, growing SMEs, multi-till operators",
    perks: [
      "Everything in Growth",
      "POS settlement-loss cover",
      "Delivery / payment mismatch",
      "Up to KES 1,000,000 cover",
    ],
  },
];

const COVERAGE = [
  {
    icon: Wallet,
    title: "M-Pesa reversal fraud",
    body: "Customer reverses a paid sale after collecting goods — we make you whole.",
  },
  {
    icon: AlertOctagon,
    title: "Phishing & account takeover",
    body: "Compromised till, fake-agent calls, SIM swaps that drain your float.",
  },
  {
    icon: Receipt,
    title: "POS settlement loss",
    body: "Card transactions captured but never settled to your bank account.",
  },
  {
    icon: UserX,
    title: "Fake customer disputes",
    body: "Chargebacks and 'never-received' claims on legitimate sales.",
  },
  {
    icon: ShoppingBag,
    title: "Social commerce scams",
    body: "Fake-buyer Instagram, WhatsApp catalogue impersonation, deposit fraud.",
  },
  {
    icon: TruckIcon,
    title: "Delivery & payment mismatch",
    body: "Rider paid out, customer never paid — Fortiqa closes the gap.",
  },
];

const FAQ = [
  {
    q: "Is Fortiqa an insurance company?",
    a: "Fortiqa is a digital cyber-insurance platform built for IRA Kenya regulatory alignment. We work alongside licensed underwriter partners and operate under industry compliance standards.",
  },
  {
    q: "How are claims reviewed?",
    a: "Every claim runs through our FlashClaim pipeline: AI-assisted OCR, fraud-signal scoring, and device-fingerprint checks. Low-risk claims auto-approve in seconds; edge cases are reviewed by a human SIU officer within minutes.",
  },
  {
    q: "Can I cancel weekly?",
    a: "Yes. Cover is billed weekly with a 7-day cooling-off on first activation. You can pause or cancel anytime from the dashboard or via WhatsApp.",
  },
  {
    q: "Do I need a website?",
    a: "No website required. WhatsApp Business, M-Pesa till, POS device, or Instagram shop — all qualify.",
  },
  {
    q: "Can WhatsApp merchants join?",
    a: "Absolutely. WhatsApp merchants are our core audience. Sign up, file claims, and receive payouts entirely over WhatsApp.",
  },
  {
    q: "Is this only for Kenya?",
    a: "Today, yes — Kenya is our launch market. Nigeria, Ghana, and Egypt are next on the roadmap.",
  },
];

/* ------------------------------------------------------------------ */
/* Reusable pieces                                                     */
/* ------------------------------------------------------------------ */

function SectionEyebrow({ children }) {
  return (
    <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#c15337]">
      {children}
    </div>
  );
}

function FAQItem({ q, a, isOpen, onToggle, testid }) {
  return (
    <div className="border-b border-[#114232]/10 py-5">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 text-left group"
        data-testid={testid}
        aria-expanded={isOpen}
      >
        <span className="font-heading text-base sm:text-lg font-bold text-[#114232] group-hover:text-[#0d3125] transition-colors">
          {q}
        </span>
        <span
          className={`w-8 h-8 rounded-full grid place-items-center shrink-0 transition-colors ${
            isOpen
              ? "bg-[#c15337] text-white"
              : "bg-[#114232]/8 text-[#114232] group-hover:bg-[#114232]/14"
          }`}
        >
          {isOpen ? (
            <Minus className="w-4 h-4" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
        </span>
      </button>
      {isOpen && (
        <p className="text-sm sm:text-base text-[#5c6360] leading-relaxed mt-3 pr-12">
          {a}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function LandingPage() {
  useTranslation(); // ensure i18n initialised
  const { user } = useAuth();
  const [openFaq, setOpenFaq] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const dashHref = user
    ? user.role === "merchant"
      ? "/merchant"
      : user.role === "partner_admin"
        ? "/partner"
        : "/app"
    : null;

  return (
    <div
      className="min-h-screen bg-[#f4f2eb] text-[#1b1d1c] scroll-smooth"
      data-testid="landing-page"
    >
      {/* ============================================================ */}
      {/* NAV                                                          */}
      {/* ============================================================ */}
      <nav
        className="sticky top-0 z-40 backdrop-blur-md bg-[#f4f2eb]/85 border-b border-[#114232]/8"
        data-testid="landing-nav"
      >
        <div className="px-5 sm:px-6 lg:px-10 max-w-7xl mx-auto h-16 sm:h-18 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2.5 shrink-0"
            data-testid="landing-brand"
          >
            <img
              src="/fortiqa-mark.svg"
              alt="Fortiqa"
              className="w-9 h-9 sm:w-10 sm:h-10 object-contain"
            />
            <span className="font-heading text-lg sm:text-xl font-bold tracking-tight text-[#114232]">
              Fortiqa
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-7 text-sm">
            <a
              href="#how"
              className="text-[#5c6360] hover:text-[#114232] transition-colors"
            >
              How it works
            </a>
            <a
              href="#tiers"
              className="text-[#5c6360] hover:text-[#114232] transition-colors"
            >
              Tiers
            </a>
            <a
              href="#coverage"
              className="text-[#5c6360] hover:text-[#114232] transition-colors"
            >
              Coverage
            </a>
            <a
              href="#claims"
              className="text-[#5c6360] hover:text-[#114232] transition-colors"
            >
              FlashClaim
            </a>
            <a
              href="#faq"
              className="text-[#5c6360] hover:text-[#114232] transition-colors"
            >
              FAQ
            </a>
            <Link
              to="/docs/embed"
              data-testid="landing-nav-developers"
              className="text-[#5c6360] hover:text-[#114232] transition-colors"
            >
              Developers
            </Link>
          </div>

          <div className="flex items-center gap-2.5">
            <div className="hidden sm:block">
              <LanguageSwitcher compact />
            </div>
            {dashHref ? (
              <Link
                to={dashHref}
                data-testid="landing-continue-to-dashboard"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#c15337] text-white text-sm font-semibold hover:bg-[#a8472f] transition-colors shadow-sm hover:shadow-md"
              >
                <LayoutDashboard className="w-3.5 h-3.5" />
                Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="landing-cta-login"
                  className="hidden sm:inline-flex items-center px-4 py-2 text-sm font-semibold text-[#114232] hover:text-[#0d3125] transition-colors"
                >
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  data-testid="landing-cta-signup"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#114232] text-[#f4f2eb] text-sm font-semibold hover:bg-[#0d3125] transition-colors shadow-sm hover:shadow-md"
                >
                  Sign up <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </>
            )}
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden w-10 h-10 rounded-full grid place-items-center text-[#114232] hover:bg-[#114232]/8 transition-colors"
              data-testid="landing-mobile-toggle"
              aria-label="Menu"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? (
                <Minus className="w-5 h-5" />
              ) : (
                <Plus className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
        {/* Mobile menu */}
        {mobileOpen && (
          <div
            className="lg:hidden border-t border-[#114232]/10 bg-[#f4f2eb]"
            data-testid="landing-mobile-menu"
          >
            <div className="px-5 py-3 flex flex-col">
              {[
                ["#how", "How it works"],
                ["#tiers", "Tiers"],
                ["#coverage", "Coverage"],
                ["#claims", "FlashClaim"],
                ["#trust-badge", "Trust Badge"],
                ["#faq", "FAQ"],
              ].map(([h, l]) => (
                <a
                  key={h}
                  href={h}
                  onClick={() => setMobileOpen(false)}
                  className="py-3 text-sm text-[#5c6360] hover:text-[#114232] border-b border-[#114232]/5 last:border-0"
                >
                  {l}
                </a>
              ))}
              <Link
                to="/docs/embed"
                onClick={() => setMobileOpen(false)}
                className="py-3 text-sm text-[#5c6360] hover:text-[#114232]"
              >
                Developers
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* ============================================================ */}
      {/* HERO                                                         */}
      {/* ============================================================ */}
      <header className="px-5 sm:px-6 lg:px-10 max-w-7xl mx-auto pt-10 sm:pt-14 lg:pt-20 pb-14 lg:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7 space-y-6 sm:space-y-7">
          {/* Premium trust badge */}
          <div
            className="inline-flex items-center gap-2 pl-2 pr-4 py-1.5 rounded-full bg-white border border-[#114232]/15 shadow-sm"
            data-testid="hero-flashclaim-badge"
          >
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#c15337]">
              <Zap className="w-3 h-3 text-white fill-white" />
            </span>
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#114232]">
              FlashClaim — paid in seconds
            </span>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold leading-[0.95] text-[#114232]">
            Cyber cover for every{" "}
            <span className="text-[#c15337]">Kenyan</span> merchant.
          </h1>

          <p className="text-base sm:text-lg text-[#5c6360] max-w-xl leading-relaxed">
            Embedded micro-insurance and fraud protection for M-Pesa merchants,
            online sellers, POS agents, and growing businesses.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="#tiers"
              data-testid="landing-cta-explore"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#114232] text-[#f4f2eb] font-semibold hover:bg-[#0d3125] transition-all shadow-sm hover:shadow-lg"
            >
              Explore cover <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="https://wa.me/254700000000?text=Niko%20tayari%20kujisajili%20Fortiqa"
              target="_blank"
              rel="noreferrer"
              data-testid="landing-cta-whatsapp"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-[#114232]/20 bg-white hover:bg-white hover:border-[#114232]/40 text-[#114232] font-semibold transition-all hover:shadow-md"
            >
              <MessageCircle className="w-4 h-4 text-[#25D366]" /> Sign up via
              WhatsApp
            </a>
          </div>

          {/* Trust pills */}
          <div
            className="flex flex-wrap gap-2 pt-3"
            data-testid="hero-trust-pills"
          >
            {TRUST_PILLS.map((p) => (
              <div
                key={p}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#114232]/10 text-xs font-semibold text-[#114232]"
              >
                <Check className="w-3 h-3 text-[#2d7a4a]" strokeWidth={3} />
                {p}
              </div>
            ))}
          </div>
        </div>

        {/* Hero card */}
        <div className="lg:col-span-5">
          <div
            className="relative rounded-3xl overflow-hidden bg-white border border-[#114232]/10 shadow-xl"
            data-testid="hero-card"
          >
            <div
              className="aspect-[4/5] sm:aspect-[5/6] bg-cover bg-center"
              style={{
                backgroundImage:
                  "url('https://images.unsplash.com/photo-1761370980657-22586ea44093?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200')",
              }}
            />
            <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 bg-gradient-to-t from-white via-white to-transparent pt-16">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#c15337] font-bold">
                  Live claim
                </div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#c15337]/10 text-[#c15337] text-[10px] font-bold uppercase tracking-wider">
                  <Zap className="w-2.5 h-2.5 fill-[#c15337]" />
                  FlashClaim 41s
                </div>
              </div>
              <div className="text-xs text-[#5c6360] mb-1 font-mono">PAYOUT</div>
              <div className="font-heading font-bold text-3xl sm:text-4xl text-[#114232] mb-3">
                KES 8,500
              </div>
              <p className="text-xs sm:text-sm text-[#5c6360] leading-relaxed">
                <strong className="text-[#114232]">Mama Wanjiru</strong> filed an
                M-Pesa fraud claim from Nairobi — paid before her chai went
                cold.
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* ============================================================ */}
      {/* HOW IT WORKS                                                 */}
      {/* ============================================================ */}
      <section
        id="how"
        className="px-5 sm:px-6 lg:px-10 max-w-7xl mx-auto py-16 lg:py-24"
        data-testid="how-section"
      >
        <div className="max-w-2xl mb-12 lg:mb-16">
          <SectionEyebrow>How Fortiqa works</SectionEyebrow>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#114232] mt-3">
            Four steps. Sixty seconds. Real cover.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {HOW_STEPS.map((s) => (
            <div
              key={s.n}
              className="rounded-2xl bg-white border border-[#114232]/10 p-6 hover:border-[#114232]/30 hover:shadow-md transition-all"
            >
              <div className="font-mono text-[#c15337] font-bold text-sm mb-4">
                {s.n}
              </div>
              <div className="font-heading text-lg font-bold text-[#114232] mb-2 leading-snug">
                {s.title}
              </div>
              <p className="text-sm text-[#5c6360] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================ */}
      {/* TIERS                                                        */}
      {/* ============================================================ */}
      <section
        id="tiers"
        className="px-5 sm:px-6 lg:px-10 max-w-7xl mx-auto py-16 lg:py-24"
        data-testid="tiers-section"
      >
        <div className="max-w-2xl mb-12 lg:mb-16">
          <SectionEyebrow>Cover tiers</SectionEyebrow>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#114232] mt-3">
            Cover starts at KES 99 / week.
          </h2>
          <p className="text-base sm:text-lg text-[#5c6360] mt-4 leading-relaxed">
            Weekly premium, paid via M-Pesa. Pause or upgrade anytime —
            seven-day cooling-off on first activation.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5 lg:gap-6">
          {TIERS.map((t) => (
            <div
              key={t.code}
              className={`relative rounded-3xl p-7 lg:p-8 flex flex-col transition-all hover:shadow-xl ${
                t.featured
                  ? "bg-[#114232] text-[#f4f2eb] shadow-lg lg:-translate-y-4"
                  : "bg-white border border-[#114232]/10 hover:border-[#114232]/30"
              }`}
              data-testid={`tier-${t.code}`}
            >
              {t.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#c15337] text-white text-[10px] font-bold uppercase tracking-wider shadow-md">
                  <Sparkles className="w-3 h-3" /> Most popular
                </div>
              )}
              <div
                className={`text-[10px] uppercase tracking-[0.22em] font-bold mb-2 ${
                  t.featured ? "text-[#d49a36]" : "text-[#c15337]"
                }`}
              >
                {t.name}
              </div>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="font-heading text-4xl sm:text-5xl font-extrabold">
                  KES {t.premium}
                </span>
                <span
                  className={`text-sm ${
                    t.featured ? "text-[#f4f2eb]/70" : "text-[#5c6360]"
                  }`}
                >
                  /week
                </span>
              </div>
              <div
                className={`text-xs mb-5 ${
                  t.featured ? "text-[#f4f2eb]/75" : "text-[#5c6360]"
                }`}
              >
                {t.bestFor}
              </div>

              <ul className="space-y-2.5 mb-7 flex-1">
                {t.perks.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm">
                    <Check
                      className={`w-4 h-4 mt-0.5 shrink-0 ${
                        t.featured ? "text-[#d49a36]" : "text-[#2d7a4a]"
                      }`}
                      strokeWidth={3}
                    />
                    <span
                      className={
                        t.featured ? "text-[#f4f2eb]/95" : "text-[#1b1d1c]"
                      }
                    >
                      {p}
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href={`https://wa.me/254700000000?text=Hi%20Fortiqa%2C%20sign%20me%20up%20for%20${encodeURIComponent(
                  t.name,
                )}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`tier-cta-${t.code}`}
                className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold transition-all ${
                  t.featured
                    ? "bg-[#c15337] hover:bg-[#a8472f] text-white shadow-md hover:shadow-lg"
                    : "bg-[#114232] hover:bg-[#0d3125] text-[#f4f2eb] shadow-sm hover:shadow-md"
                }`}
              >
                <MessageCircle className="w-4 h-4" />
                Sign up via WhatsApp
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================ */}
      {/* COVERAGE                                                     */}
      {/* ============================================================ */}
      <section
        id="coverage"
        className="px-5 sm:px-6 lg:px-10 max-w-7xl mx-auto py-16 lg:py-24"
        data-testid="coverage-section"
      >
        <div className="max-w-2xl mb-12 lg:mb-16">
          <SectionEyebrow>What is covered</SectionEyebrow>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#114232] mt-3">
            Real risks. Real cover.
          </h2>
          <p className="text-base sm:text-lg text-[#5c6360] mt-4 leading-relaxed">
            We protect against the fraud merchants actually face — not generic
            risks lifted from a corporate-liability template.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {COVERAGE.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-2xl bg-white border border-[#114232]/10 p-6 hover:border-[#114232]/30 hover:shadow-md transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-[#c15337]/10 grid place-items-center mb-4">
                <Icon className="w-5 h-5 text-[#c15337]" strokeWidth={1.7} />
              </div>
              <div className="font-heading text-base sm:text-lg font-bold text-[#114232] mb-1.5">
                {title}
              </div>
              <p className="text-sm text-[#5c6360] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================ */}
      {/* TRUST BADGE                                                  */}
      {/* ============================================================ */}
      <section
        id="trust-badge"
        className="px-5 sm:px-6 lg:px-10 max-w-7xl mx-auto py-16 lg:py-24"
        data-testid="trust-badge-section"
      >
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <SectionEyebrow>Merchant Trust Badge</SectionEyebrow>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#114232] mt-3 leading-[1.05]">
              Show customers you've got their back.
            </h2>
            <p className="text-base sm:text-lg text-[#5c6360] mt-5 leading-relaxed">
              Verified Fortiqa merchants can display the Trust Badge on their
              website, WhatsApp catalogue, Instagram shop, or checkout page —
              proof that a cyber-cover policy is active and claims will be
              honoured.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Live-verified — badge auto-revokes if cover lapses",
                "One-line embed: HTML snippet or static PNG",
                "Customers can click through to verify the policy",
              ].map((p) => (
                <li
                  key={p}
                  className="flex items-start gap-2.5 text-sm sm:text-base text-[#1b1d1c]"
                >
                  <BadgeCheck
                    className="w-5 h-5 text-[#2d7a4a] mt-0.5 shrink-0"
                    strokeWidth={2}
                  />
                  {p}
                </li>
              ))}
            </ul>
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 mt-8 px-5 py-3 rounded-full bg-[#114232] text-[#f4f2eb] font-semibold hover:bg-[#0d3125] transition-all shadow-sm hover:shadow-md"
              data-testid="trust-badge-cta"
            >
              Get your badge <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Badge mockup */}
          <div className="relative">
            <div className="rounded-3xl bg-white border border-[#114232]/10 p-7 sm:p-10 shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <img
                  src="/fortiqa-mark.svg"
                  alt="Fortiqa"
                  className="w-14 h-14 object-contain"
                />
                <div>
                  <div className="font-heading font-bold text-xl text-[#114232]">
                    Fortiqa Verified
                  </div>
                  <div className="text-xs text-[#5c6360] font-mono">
                    fortiqa.co.ke/verify/MW-2026
                  </div>
                </div>
              </div>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#5c6360]">Merchant</span>
                  <span className="font-semibold text-[#114232]">
                    Mama Wanjiru Boutique
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5c6360]">Cover tier</span>
                  <span className="font-semibold text-[#114232]">Growth</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5c6360]">Active since</span>
                  <span className="font-semibold text-[#114232]">
                    Aug 2025
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5c6360]">Status</span>
                  <span className="inline-flex items-center gap-1.5 font-semibold text-[#2d7a4a]">
                    <span className="w-2 h-2 rounded-full bg-[#2d7a4a] animate-pulse" />
                    Live
                  </span>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-[#114232]/10 text-xs text-[#5c6360] text-center">
                Click to verify this policy on fortiqa.co.ke
              </div>
            </div>
            {/* decorative dot */}
            <div className="absolute -top-4 -right-4 w-12 h-12 rounded-full bg-[#c15337]/15 -z-10" />
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* CLAIMS / FLASHCLAIM                                          */}
      {/* ============================================================ */}
      <section
        id="claims"
        className="px-5 sm:px-6 lg:px-10 max-w-7xl mx-auto py-16 lg:py-24"
        data-testid="claims-section"
      >
        <div className="bg-[#114232] rounded-3xl p-8 sm:p-10 lg:p-16 text-[#f4f2eb] grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#d49a36]">
              FlashClaim
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.05] mt-3">
              Paid in seconds, reviewed with intelligence.
            </h2>
            <p className="text-base sm:text-lg text-[#f4f2eb]/85 mt-5 leading-relaxed max-w-xl">
              Merchants submit claims through WhatsApp — screenshots,
              transaction IDs, and any evidence. Our pipeline scores the claim
              in real time, auto-approves the clean ones, and routes edge cases
              to a human SIU officer within minutes.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              {[
                { v: "< 90s", l: "Median FlashClaim payout" },
                { v: "94%", l: "Auto-decision rate" },
                { v: "24/7", l: "WhatsApp intake" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-heading text-3xl font-extrabold text-[#d49a36]">
                    {s.v}
                  </div>
                  <div className="text-xs text-[#f4f2eb]/70 mt-1 uppercase tracking-wider">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl bg-[#0d3125] p-5 sm:p-6 border border-[#f4f2eb]/10">
              <div className="space-y-3.5 text-sm">
                {[
                  { who: "Wanjiru", time: "09:41", msg: "Mteja amerudia M-Pesa baada ya kuchukua bidhaa. Hii ni screenshot.", side: "in" },
                  { who: "Fortiqa", time: "09:41", msg: "✓ Asante. Claim FQC-8821 imefunguliwa. Tunachunguza.", side: "out" },
                  { who: "Fortiqa", time: "09:42", msg: "⚡ FlashClaim approved. KES 8,500 inakuja kwa till yako sasa.", side: "out" },
                ].map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.side === "out" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                        m.side === "out"
                          ? "bg-[#d49a36]/15 text-[#f4f2eb] rounded-br-sm"
                          : "bg-[#f4f2eb]/8 text-[#f4f2eb]/95 rounded-bl-sm"
                      }`}
                    >
                      <div className="text-[10px] uppercase tracking-wider text-[#f4f2eb]/50 mb-1 font-mono">
                        {m.who} · {m.time}
                      </div>
                      <div className="text-sm leading-relaxed">{m.msg}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* COMPLIANCE                                                   */}
      {/* ============================================================ */}
      <section
        id="compliance"
        className="px-5 sm:px-6 lg:px-10 max-w-7xl mx-auto py-16 lg:py-24"
        data-testid="compliance-section"
      >
        <div className="max-w-2xl mb-12 lg:mb-16">
          <SectionEyebrow>Compliance</SectionEyebrow>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#114232] mt-3">
            Built for regulated growth.
          </h2>
          <p className="text-base sm:text-lg text-[#5c6360] mt-4 leading-relaxed">
            Fortiqa is built for IRA Kenya regulatory alignment from day one —
            with KYC, fraud scoring, full policy wording, an immutable claims
            audit trail, and partner-underwriter readiness for licensed
            distribution.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          {[
            {
              icon: ShieldCheck,
              title: "IRA-aligned structure",
              body: "Policy wording, premiums, and claims pipeline reviewed against IRA distribution rules.",
            },
            {
              icon: BadgeCheck,
              title: "Tiered KYC",
              body: "ID + selfie + business verification scaled to cover tier and claim amount.",
            },
            {
              icon: FileSearch,
              title: "Claims audit trail",
              body: "Every action — submission, decision, payout — is signed and immutable for regulator review.",
            },
            {
              icon: TrendingUp,
              title: "Underwriter-ready",
              body: "Solvency-ratio reporting, claims-ratio dashboards, and IRA quarterly PDF bundles built-in.",
            },
          ].map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-2xl bg-white border border-[#114232]/10 p-6 hover:border-[#114232]/30 hover:shadow-md transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-[#114232]/8 grid place-items-center mb-4">
                <Icon className="w-5 h-5 text-[#114232]" strokeWidth={1.7} />
              </div>
              <div className="font-heading text-base font-bold text-[#114232] mb-1.5">
                {title}
              </div>
              <p className="text-sm text-[#5c6360] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================ */}
      {/* DASHBOARD PREVIEW                                            */}
      {/* ============================================================ */}
      <section
        id="dashboard"
        className="px-5 sm:px-6 lg:px-10 max-w-7xl mx-auto py-16 lg:py-24"
        data-testid="dashboard-section"
      >
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-5">
            <SectionEyebrow>Merchant dashboard</SectionEyebrow>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#114232] mt-3 leading-[1.05]">
              Everything you need in one place.
            </h2>
            <p className="text-base sm:text-lg text-[#5c6360] mt-5 leading-relaxed">
              Track your cover, see your CyberScore risk rating, monitor
              FlashClaim payouts, and respond to fraud alerts the second they
              happen.
            </p>
            <ul className="mt-7 space-y-2.5">
              {[
                "Active cover & tier",
                "Claims submitted & status",
                "CyberScore risk rating",
                "Merchant Trust Badge status",
                "Weekly payment history",
                "Live fraud alerts",
              ].map((p) => (
                <li
                  key={p}
                  className="flex items-center gap-2.5 text-sm text-[#1b1d1c]"
                >
                  <Check
                    className="w-4 h-4 text-[#2d7a4a]"
                    strokeWidth={3}
                  />
                  {p}
                </li>
              ))}
            </ul>
          </div>

          {/* Dashboard mockup */}
          <div className="lg:col-span-7">
            <div
              className="rounded-3xl bg-white border border-[#114232]/10 shadow-xl p-5 sm:p-7"
              data-testid="dashboard-mockup"
            >
              {/* Top bar */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2.5">
                  <img
                    src="/fortiqa-mark.svg"
                    alt=""
                    className="w-7 h-7 object-contain"
                  />
                  <div className="font-heading font-bold text-[#114232]">
                    Fortiqa
                  </div>
                </div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#2d7a4a]/10 text-[#2d7a4a] text-[10px] font-bold uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2d7a4a] animate-pulse" />
                  Live
                </div>
              </div>

              {/* Top tiles */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
                {[
                  {
                    label: "Active cover",
                    value: "Growth",
                    sub: "KES 250k limit",
                  },
                  {
                    label: "CyberScore",
                    value: "92",
                    sub: "Low risk",
                    accent: true,
                  },
                  {
                    label: "Trust Badge",
                    value: "Verified",
                    sub: "Live · MW-2026",
                  },
                ].map((t) => (
                  <div
                    key={t.label}
                    className={`rounded-xl p-3.5 ${
                      t.accent
                        ? "bg-[#114232] text-[#f4f2eb]"
                        : "bg-[#f4f2eb] border border-[#114232]/8"
                    }`}
                  >
                    <div
                      className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${
                        t.accent ? "text-[#d49a36]" : "text-[#5c6360]"
                      }`}
                    >
                      {t.label}
                    </div>
                    <div
                      className={`font-heading font-bold text-xl ${
                        t.accent ? "text-[#f4f2eb]" : "text-[#114232]"
                      }`}
                    >
                      {t.value}
                    </div>
                    <div
                      className={`text-[11px] mt-0.5 ${
                        t.accent ? "text-[#f4f2eb]/70" : "text-[#5c6360]"
                      }`}
                    >
                      {t.sub}
                    </div>
                  </div>
                ))}
              </div>

              {/* Claims list */}
              <div className="rounded-xl bg-[#f4f2eb] border border-[#114232]/8 p-4 mb-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#5c6360] mb-3">
                  Recent claims
                </div>
                <div className="space-y-2.5">
                  {[
                    {
                      id: "FQC-8821",
                      amt: "KES 8,500",
                      status: "Paid",
                      color: "text-[#2d7a4a]",
                      dot: "bg-[#2d7a4a]",
                    },
                    {
                      id: "FQC-8804",
                      amt: "KES 3,200",
                      status: "Paid",
                      color: "text-[#2d7a4a]",
                      dot: "bg-[#2d7a4a]",
                    },
                    {
                      id: "FQC-8772",
                      amt: "KES 12,000",
                      status: "In review",
                      color: "text-[#d49a36]",
                      dot: "bg-[#d49a36]",
                    },
                  ].map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between text-sm"
                    >
                      <div className="flex items-center gap-2.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                        <span className="font-mono text-xs text-[#114232]">
                          {c.id}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-[#5c6360] text-xs hidden sm:inline">
                          {c.amt}
                        </span>
                        <span
                          className={`text-xs font-semibold ${c.color}`}
                        >
                          {c.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fraud alert */}
              <div className="rounded-xl bg-[#c15337]/8 border border-[#c15337]/20 p-3.5 flex items-start gap-3">
                <AlertOctagon className="w-4 h-4 text-[#c15337] mt-0.5 shrink-0" />
                <div className="text-xs text-[#114232] leading-relaxed">
                  <strong>Fraud alert</strong> — unusual SIM-swap pattern
                  flagged on linked till 0712-xx-xxxx.{" "}
                  <span className="text-[#c15337] font-semibold">Review</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* FAQ                                                          */}
      {/* ============================================================ */}
      <section
        id="faq"
        className="px-5 sm:px-6 lg:px-10 max-w-3xl mx-auto py-16 lg:py-24"
        data-testid="faq-section"
      >
        <div className="mb-10 lg:mb-14">
          <SectionEyebrow>FAQ</SectionEyebrow>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#114232] mt-3">
            Questions, answered.
          </h2>
        </div>
        <div>
          {FAQ.map((f, i) => (
            <FAQItem
              key={f.q}
              q={f.q}
              a={f.a}
              isOpen={openFaq === i}
              onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
              testid={`faq-q-${i}`}
            />
          ))}
        </div>
      </section>

      {/* ============================================================ */}
      {/* FOOTER CTA                                                   */}
      {/* ============================================================ */}
      <section className="px-5 sm:px-6 lg:px-10 max-w-7xl mx-auto pb-20">
        <div className="bg-[#114232] rounded-3xl p-8 sm:p-10 lg:p-16 text-[#f4f2eb] flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#d49a36] mb-2">
              Ready when you are
            </div>
            <h3 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
              Start covering your business today.
            </h3>
            <p className="text-[#f4f2eb]/80 mt-3 max-w-lg text-sm sm:text-base">
              No paperwork. No agents. Just KES 99 a week — paid via M-Pesa.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <Link
              to="/signup"
              data-testid="landing-footer-cta"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#c15337] hover:bg-[#a8472f] text-white font-semibold transition-all shadow-md hover:shadow-lg"
            >
              Get covered <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="https://wa.me/254700000000?text=Niko%20tayari%20kujisajili%20Fortiqa"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white/10 hover:bg-white/15 text-[#f4f2eb] font-semibold transition-all border border-white/20"
            >
              <MessageCircle className="w-4 h-4 text-[#25D366]" />
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
